import { Grid, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import React, { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useAuthContext } from "../../providers/Auth/auth.provider";
import { AvailablePlatform, usePlatformContext } from "../../providers/Auth/platform.provider";
import { Colors, PrimaryColors } from "../../style/Colors";
import {
  AvailablePomRoutes,
  AvailableBaseRoutes,
} from "../../utils/constants";
import { IShortcut, ShortcutType } from "../../utils/shortcuts";
import { isAdmin, isPomAgency, isPomMainContact } from "../../domain/user/user";
import { PlatformContainerTitle } from "./platform-container-title.component";

const defaultBorder = "1px solid #ffffff";

const useStyles = makeStyles()((theme) => ({
  platformContainer: {
    background: Colors.grayLight,
    cursor: "pointer",
    padding: 24,
    borderRadius: 4,
    border: defaultBorder,
    display: "flex",
  },
  cardContentText: {
    minHeight: 95,
    [theme.breakpoints.down("sm")]: {
      paddingTop: 32,
    },
  },
  cardContentImage: {
    minHeight: 95,
    alignSelf: "center",
  },
  shortcutContainer: {
    padding: 0,
  },
  listContainerGrid: {
    overflow: "hidden",
    borderRadius: 4,
    borderBottom: defaultBorder,
    [theme.breakpoints.down("sm")]: {
      minHeight: 63,
    },
  },
  listContainer: {
    padding: 0,
    margin: 0,
    borderRadius: 4,
    [theme.breakpoints.down("sm")]: {
      minHeight: 63,
    },
  },
  listItem: {
    height: 63,
    borderRadius: 4,
    background: Colors.grayLight,
    border: defaultBorder,
    "&:nth-child(4)": {
      borderTop: defaultBorder,
      borderLeft: defaultBorder,
      borderRight: defaultBorder,
      borderBottom: "none",
    },
  },
  listItemText: {
    "& .MuiTypography-root": {
      fontSize: 16,
    },
  },
  iconStyle: {
    color: PrimaryColors.base,
    height: 25,
    width: 25,
  },
}));

interface IPlatformContainerProps {
  title: string;
  Logo: FunctionComponent;
  platform: AvailablePlatform;
  shortcuts: IShortcut[];
  subTitle?: string;
}

export const PlatformContainer: React.VFC<IPlatformContainerProps> = (props) => {
  const { classes } = useStyles();
  const { title, Logo, platform, shortcuts, subTitle } = props;
  const { t } = useTranslation();
  const { internalUser } = useAuthContext();
  const history = useHistory();
  const { setActivePlatform } = usePlatformContext();

  const handleListItemClick = (shortcut: IShortcut) => {
    setActivePlatform(platform);

    const route =
      shortcut.name === ShortcutType.GroupOverview
        ? shortcut.route.replace(":erpNumber", internalUser?.group?.erpNumber ?? "")
        : shortcut.route;

    history.push(route);
  };

  const allowedShortcuts = useMemo(() => {
    const allowedShortcuts = shortcuts.filter((shortcut) => {
      const allowedRole = shortcut.mandatoryPomMainContact;
      // Filter allowedRole first
      if (allowedRole && !isPomMainContact(internalUser)) {
        return false;
      }

      return shortcut.display;
    });
    return allowedShortcuts;
  }, [internalUser, shortcuts]);

  const handlePlatformSelection = (): void => {
    setActivePlatform(platform);
    if (platform === AvailablePlatform.Pom) {
      if (isAdmin(internalUser) || isPomAgency(internalUser)) {
        history.push(AvailableBaseRoutes.GroupList);
      } else {
        history.push(AvailablePomRoutes.Reporting);
      }
      return;
    }
  };

  return (
    <Grid container id="platform-container">
      <Grid item xs={12}>
        <PlatformContainerTitle
          handleSelection={handlePlatformSelection}
          Logo={Logo}
          title={title}
          subTitle={subTitle}
        />
      </Grid>
      {allowedShortcuts.length > 0 && (
        <Grid item xs={12} id="platform-shortcuts" className={classes.listContainerGrid}>
          <List className={classes.listContainer}>
            {allowedShortcuts.map((shortcut: IShortcut, index: number) => {
              const ShortcutIcon = shortcut.icon;
              return (
                <ListItemButton
                  key={index}
                  dense
                  onClick={() => handleListItemClick(shortcut)}
                  className={classes.listItem}
                >
                  <ListItemIcon>
                    <ShortcutIcon svgColor="green" className={classes.iconStyle} viewBox="0 0 25 25" />
                  </ListItemIcon>
                  <ListItemText primary={t(shortcut.translation)} className={classes.listItemText} />
                </ListItemButton>
              );
            })}
          </List>
        </Grid>
      )}
    </Grid>
  );
};
